// src/components/Assessment/Results.jsx

import React from 'react';
import {
  VStack,
  Heading,
  Text,
  Divider,
  HStack,
  Button,
  Badge,
  Icon,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react';
import { FaCheckCircle, FaClock, FaRocket, FaGraduationCap, FaBriefcase } from 'react-icons/fa';

const Results = ({ recommendation, onReset, onViewDetails, answers, isLoading }) => {
  // All hooks must be called at the top level
  const textColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");
  const messageBgColor = useColorModeValue("gray.50", "gray.700"); // Moved this hook to top level

  if (isLoading) {
    return (
      <VStack align="center" spacing={6} py={8}>
        <Spinner size="xl" color="green.500" />
        <Heading size="lg" color={textColor}>Analyzing your assessment...</Heading>
        <Text color={textColor}>Creating your personalized learning plan</Text>
      </VStack>
    );
  }

  if (!recommendation) {
    return (
      <VStack align="center" spacing={6} py={8}>
        <Heading size="lg" color={textColor}>Unable to generate recommendation</Heading>
        <Text color={textColor}>Please try again or contact support</Text>
        <Button onClick={onReset} colorScheme="green">
          Retry Assessment
        </Button>
      </VStack>
    );
  }

  const getReasoningText = () => {
    const { track, reasoning, confidence } = recommendation;
    
    const reasoningMap = {
      "strong_experience": {
        title: "You're Ready to Accelerate Your AI Career",
        subtitle: "Your background shows strong foundation for advanced learning",
        points: [
          "You have solid coding experience that will help you grasp advanced AI concepts faster",
          "Your project background indicates you're ready for real-world AI applications",
          "With your current skill level, you can skip basics and dive straight into building impactful AI projects",
          "You'll benefit from advanced mentorship and complex project work that matches your capability"
        ],
        icon: FaRocket,
        color: "purple"
      },
      "moderate_experience": {
        title: "You're Well-Positioned for Advanced AI Learning",
        subtitle: "Your mixed experience profile suggests you'll thrive with structured acceleration",
        points: [
          "You have enough foundation to handle accelerated learning without getting overwhelmed",
          "The experienced track will challenge you appropriately while building on your existing knowledge",
          "You'll bridge any skill gaps quickly while working on meaningful AI projects",
          "This path maximizes your time by focusing on practical, career-relevant AI skills"
        ],
        icon: FaBriefcase,
        color: "blue"
      },
      "moderate_beginner": {
        title: "Build Strong AI Foundations for Long-Term Success",
        subtitle: "Starting with fundamentals will create unshakable confidence in AI",
        points: [
          "You'll build coding and math foundations that make advanced AI concepts easier to understand",
          "Step-by-step project progression ensures you never feel lost or overwhelmed",
          "The beginner track is designed specifically for people with your background to succeed",
          "You'll develop real AI projects with proper guidance, avoiding common beginner frustrations"
        ],
        icon: FaGraduationCap,
        color: "green"
      },
      "strong_beginner": {
        title: "Start Your AI Journey on the Right Foot",
        subtitle: "Perfect foundation-building track for complete beginners",
        points: [
          "We start from absolute basics and build up to real AI projects at your pace",
          "You'll learn essential programming and math concepts without assuming prior knowledge",
          "The curriculum is designed to build confidence through small, achievable milestones",
          "By the end, you'll have solid AI foundations and a complete project for your portfolio"
        ],
        icon: FaClock,
        color: "orange"
      },
      "fallback_experienced": {
        title: "Experienced Track Recommended",
        subtitle: "Your profile suggests you're ready for accelerated learning",
        points: [
          "You have the foundational skills needed for advanced AI concepts",
          "The experienced track will help you build on your existing knowledge",
          "You'll work on real-world projects with industry mentorship",
          "Perfect for achieving your career goals efficiently"
        ],
        icon: FaRocket,
        color: "purple"
      },
      "fallback_beginner": {
        title: "Beginner Track Recommended",
        subtitle: "Build strong foundations for your AI journey",
        points: [
          "Start with fundamentals and build up to real AI projects",
          "Step-by-step guidance ensures you never feel overwhelmed",
          "Perfect for building confidence and practical skills",
          "Designed for complete beginners to succeed"
        ],
        icon: FaGraduationCap,
        color: "green"
      }
    };

    return reasoningMap[reasoning] || reasoningMap["fallback_beginner"];
  };

  const handleViewDetails = () => {
    const path = recommendation.track === "BEGINNER" ? "/beginner-track" : "/experienced-track";
    if (onViewDetails) {
      onViewDetails(path);
    }
  };

  const reasoningData = getReasoningText();
  const IconComponent = reasoningData.icon;

  return (
    <VStack align="start" spacing={6}>
      <HStack spacing={4} align="center">
        <Icon as={IconComponent} color={`${reasoningData.color}.500`} boxSize={8} />
        <VStack align="start" spacing={0}>
          <Heading size="lg" color={textColor}>
            {reasoningData.title}
          </Heading>
          <Text color={secondaryTextColor} fontSize="sm">
            {reasoningData.subtitle}
          </Text>
        </VStack>
      </HStack>

      <Badge colorScheme={reasoningData.color} fontSize="sm" px={3} py={1}>
        {recommendation.confidence === "HIGH" ? "Strong Match" : "Good Match"} • {recommendation.track} Track
      </Badge>

      <VStack align="start" spacing={3}>
        {reasoningData.points.map((point, index) => (
          <HStack key={index} align="start" spacing={3}>
            <Icon as={FaCheckCircle} color="green.500" boxSize={4} mt={1} />
            <Text color={textColor}>{point}</Text>
          </HStack>
        ))}
      </VStack>

      {recommendation.personalizedMessage && (
        <Text color={textColor} fontStyle="italic" bg={messageBgColor} p={3} borderRadius="md">
          {recommendation.personalizedMessage}
        </Text>
      )}

      <Divider />

      {/* Personalized based on their answers */}
      <VStack align="start" spacing={3}>
        <Heading size="md" color={textColor}>Your Learning Path Includes:</Heading>
        {recommendation.track === "BEGINNER" ? (
          <>
            <Text color={textColor}>• Foundational Python & AI concepts</Text>
            <Text color={textColor}>• Step-by-step project building</Text>
            <Text color={textColor}>• Daily coding practice routines</Text>
            <Text color={textColor}>• Portfolio project guidance</Text>
            {answers.timeAvailability === "1to2" && (
              <Text color={textColor}>• Flexible schedule perfect for {answers.timeAvailability.replace("to", "-")} hours daily</Text>
            )}
          </>
        ) : (
          <>
            <Text color={textColor}>• Advanced AI model implementation</Text>
            <Text color={textColor}>• Real-world project deployment</Text>
            <Text color={textColor}>• Industry mentorship</Text>
            <Text color={textColor}>• Career acceleration strategies</Text>
            {answers.goal === "getJob" && (
              <Text color={textColor}>• Job preparation and interview training</Text>
            )}
          </>
        )}
      </VStack>

      <Button
        colorScheme="green"
        size="lg"
        onClick={handleViewDetails}
        width="100%"
      >
        View {recommendation.track} Track Details
      </Button>

      <Button variant="outline" onClick={onReset} width="100%">
        Retake Assessment
      </Button>
    </VStack>
  );
};

export default Results;